import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import Header from "../components/layout/header/Header";

import { getBlogs } from "../api/blog/blogApi";
import Footer from "../components/layout/footer/Footer";
import { Bell, ChevronRight, ChevronLeft } from "lucide-react";
import "../components/layout/blog/BlogListPage.css";
import { subscribe } from "../api/Subsciption/subscriptionApi";
import { Helmet } from "react-helmet-async";
 const BLOGS_ENABLED = false;
const categories = [
  {
    label: "All Articles",
    value: "",
  },
  {
    label: "AI Insights",
    value: "ai-insights",
  },
  {
    label: "Case Studies",
    value: "case-studies",
  },
  {
    label: "Technical",
    value: "technical",
  },
  {
    label: "Strategy",
    value: "strategy",
  },
];
interface Blog {
  id: string;
  title: string;
  slug: string;
  image: string;
  description: string;
  category: string;
  author: {
    name: string;
    avatar: string;
    role?: string;
  };
  readTime: string;
  publishedDate: string;
  featured?: boolean;
}
const BlogListPage: React.FC = () => {
  const navigate = useNavigate();

  const [blogs, setBlogs] = useState<Blog[]>([]);


  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [totalPages, setTotalPages] = useState(1);

  const [search, setSearch] = useState("");

  const [activeCategory, setActiveCategory] = useState("");
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState("");
  const [subscribeError, setSubscribeError] = useState("");
  const [apiUnavailable, setApiUnavailable] = useState(false);
 
useEffect(() => {
  if (!BLOGS_ENABLED) {
    setBlogs([]);
    setApiUnavailable(true);
    return;
  }

  const loadBlogs = async () => {
    try {
      setApiUnavailable(false);

      const response = await getBlogs({
        page,
        limit,
        category: activeCategory || undefined,
        q: search || undefined,
      });

      const mappedBlogs = response.data.map((blog: any) => ({
        ...blog,
        image: blog.coverImage,
        description: blog.excerpt,
        category: blog.category?.title || "General",
        publishedDate: new Date(blog.publishedAt).toLocaleDateString(),
        featured: Boolean(blog.featured),
        author: {
          name: blog.author?.name || "Unknown",
          avatar: blog.author?.avatar || "",
          role: "Author",
        },
        readTime: `${blog.readTime} min read`,
      }));

      const uniqueBlogs = Array.from(
        new Map(
          mappedBlogs.map((blog: Blog) => [
            blog.title.trim().toLowerCase(),
            blog,
          ])
        ).values()
      );

      setBlogs(uniqueBlogs);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      console.error(err);
      setBlogs([]);
      setApiUnavailable(true);
    }
  };

  loadBlogs();
}, [page, limit, activeCategory, search]);

  const featuredBlog = useMemo(
    () => blogs.find((blog) => blog.featured) ?? blogs[0],
    [blogs],
  );

  const gridBlogs = useMemo(
    () => blogs.filter((blog) => blog.slug !== featuredBlog?.slug),
    [blogs, featuredBlog],
  );
  const handleSubscribe = async () => {
    setSubscribeMessage("");
    setSubscribeError("");

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setSubscribeError("Please enter your email.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(trimmedEmail)) {
      setSubscribeError("Please enter a valid email.");
      return;
    }

    try {
      setIsSubscribing(true);

      await subscribe({
        email: trimmedEmail,
        type: "newsletter",
      });

      setSubscribeMessage("Successfully subscribed!");
      setEmail("");
    } catch (error: any) {
      setSubscribeError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Subscription failed. Please try again.",
      );
    } finally {
      setIsSubscribing(false);
    }
  };



  return (
    <div className="page-shell page-shell--blog">
      <Helmet>
        <title>
          {featuredBlog 
            ? `Insights & Articles: ${featuredBlog.title}` 
            : "Digital Presence Insights & Strategy Blog"}
        </title>
        <meta 
          name="description" 
          content={
            featuredBlog 
              ? featuredBlog.description 
              : "Explore our latest bi-weekly intelligence briefings, deep dives, and expert commentary on unlocking growth levers and optimization strategy."
          } 
        />
      </Helmet>
      <Header />
      <main className="blog-list-page">
        <div className="blog-page__inner">
          {/* ================= HERO ================= */}

          {featuredBlog && featuredBlog &&(
            <section
              className="featured-blog featured-section"
              onClick={() => navigate(`/blogs/${featuredBlog.slug}`)}
            >
              <div className="featured-image">
                <img src={featuredBlog.image} alt={featuredBlog.title} />
              </div>

              <div className="featured-content">
                <span className="featured-badge">
                  {featuredBlog.category.toUpperCase()}
                </span>

                <h1>{featuredBlog.title}</h1>

                <p>{featuredBlog.description}</p>

                <div className="featured-divider" />

                <div className="featured-author">
                  <img
                    src={featuredBlog.author.avatar}
                    alt={featuredBlog.author.name}
                  />

                  <div className="featured-meta">
                    <strong>{featuredBlog.author.name}</strong>

                    <span>
                      {featuredBlog.author.role} • {featuredBlog.readTime}
                    </span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ================= TOOLBAR ================= */}
{!apiUnavailable  && blogs.length > 0 &&(
  <section className="blog-toolbar toolbar-section">
    {/* Desktop Tabs */}
    <div className="category-tabs desktop-tabs">
      {categories.map((category) => (
        <button
          key={category.value}
          className={activeCategory === category.value ? "active" : ""}
          onClick={() => {
            setActiveCategory(category.value);
            setPage(1);
          }}
        >
          {category.label}
        </button>
      ))}
    </div>

    {/* Mobile Dropdown */}
    <div className="mobile-category">
      <select
        value={activeCategory}
        onChange={(e) => {
          setActiveCategory(e.target.value);
          setPage(1);
        }}
      >
        {categories.map((category) => (
          <option key={category.value} value={category.value}>
            {category.label}
          </option>
        ))}
      </select>
    </div>

    {/* Search */}
    <div className="search-box">
      <input
        type="text"
        placeholder="Search articles..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(1);
        }}
      />
      <Search size={18} />
    </div>
  </section>
)}

          {/* ================= BLOG GRID ================= */}
{apiUnavailable ? (
  <section className="blog-empty-state">
    <div className="empty-content">
      <h2>No Articles Yet</h2>

      <p>
       We haven't published any articles yet.
      </p>

      <p>
       Check back soon for updates, news, and helpful resources.
      </p>

      <button onClick={() => navigate("/")}>
        Back to Home
      </button>
    </div>
  </section>
) : (
          <section className="blog-grid">
            {gridBlogs.map((blog, index) => (
              <React.Fragment key={blog.id}>
                <article
                  className="blog-card"
                  onClick={() => navigate(`/blogs/${blog.slug}`)}
                >
                  <div className="card-image">
                    <img src={blog.image} alt={blog.title} />

                    <span className="category">
                      {blog.category.toUpperCase()}
                    </span>
                  </div>

                  <div className="card-content">
                    <h3>{blog.title}</h3>

                    <p>{blog.description}</p>

                    <div className="card-bottom">
                      <div className="author">
                        <img src={blog.author.avatar} alt={blog.author.name} />

                        <span>{blog.author.name}</span>
                      </div>

                      <span>
                        {blog.publishedDate} • {blog.readTime}
                      </span>
                    </div>
                  </div>
                </article>
                {page === 1 && index === 2 && (
                  <div className="newsletter-card">
                    <div className="newsletter-icon">
                      <Bell size={36} />
                    </div>

                    <h3>Join the Future</h3>

                    <p>
                      Get bi-weekly intelligence briefings delivered straight to
                      your inbox.
                    </p>

                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />

                    <button onClick={handleSubscribe} disabled={isSubscribing}>
                      {isSubscribing ? "Subscribing..." : "Subscribe"}
                    </button>
                    {subscribeMessage && (
                      <p className="subscribe-success">{subscribeMessage}</p>
                    )}

                    {subscribeError && (
                      <p className="subscribe-error">{subscribeError}</p>
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
          </section>
)}
          {/* ================= PAGINATION ================= */}
{!apiUnavailable && blogs.length > 0 && (
          <div className="pagination">
            <button
              disabled={page === 1}
              onClick={() => setPage((prev) => prev - 1)}
            >
              <ChevronLeft size={18} />
            </button>

            {Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = index + 1;

              return (
                <button
                  key={pageNumber}
                  className={page === pageNumber ? "active" : ""}
                  onClick={() => setPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}

            <button
              disabled={page === totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              <ChevronRight size={18} />
            </button>
          </div>
)}</div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogListPage;
